import React, { useEffect,useState } from "react";
import { Dialog, DialogContent, DialogTitle, Box, Typography, TextField, Button, IconButton, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
    createVillageAction,
    clearCreateVillageAction,
    updateVillageAction,
    clearUpdateVillageAction,
    getVillageByIdAction,
    getAllMandalsAction,
} from "../../../store/actions";


const AddVillage = ({ open, onClose, isEdit, villageId, onSuccess, mandalId, allowedMandals }) => {
    const dispatch = useDispatch();
    const [duplicateError, setDuplicateError] = useState("");
    // Reference lists
    const { successData: mandalsData, loading: mandalsLoading } = useSelector((state) => state.getAllMandalsReducer || {});

    // Create/Update/ById reducers
    const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createVillageReducer || {});
    const { successData: villageByIdData, loading: villageByIdLoading, error: villageByIdError } = useSelector((state) => state.getVillageByIdReducer || {});
    const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateVillageReducer || {});

    const mandals = React.useMemo(() => {
        const list = Array.isArray(mandalsData?.data?.mandals)
            ? mandalsData.data.mandals
            : Array.isArray(mandalsData)
            ? mandalsData
            : [];
        if (Array.isArray(allowedMandals) && allowedMandals.length > 0) {
            return list.filter(m => allowedMandals.includes(String(m.mandalId || m.id || m.mandal_id || m.mandalID)));
        }
        return list;
    }, [mandalsData, allowedMandals]);

    const villageSchema = Yup.object({
        name: Yup.string().trim().required("Village name is required"),
        mandalId: Yup.string().trim().required("Mandal ID is required"),
    });

    // Helper to normalize village names (remove spaces, lowercase)
    const normalizeVillageName = (name) => (name || "").replace(/\s+/g, "").toLowerCase();

    // Get all villages for duplicate check
    const { successData: villagesData } = useSelector((state) => state.getAllVillagesReducer || {});
    const villages = React.useMemo(() => {
        if (Array.isArray(villagesData)) return villagesData;
        if (villagesData?.data && Array.isArray(villagesData.data.villages)) return villagesData.data.villages;
        return [];
    }, [villagesData]);

    const formik = useFormik({
        initialValues: { name: "", mandalId: mandalId ? String(mandalId) : "" },
        validationSchema: villageSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const newNameNorm = normalizeVillageName(values.name);
            const duplicate = villages.some((v) => {
                const vName = v?.name || v?.village_name || v?.villageName || "";
                const vMandalId = v?.mandalId || v?.mandal_id || v?.mandalID;
                const vId = v?.villageId || v?.id || v?.village_id;
                if (isEdit && String(vId) === String(villageId)) return false;
                return String(vMandalId) === String(values.mandalId) && normalizeVillageName(vName) === newNameNorm;
            });
            if (duplicate) {
                setDuplicateError("Village with this name already exists in this mandal!");
                return;
            }
            setDuplicateError("");
            const payload = { name: values.name, mandalId: Number(values.mandalId) };
            if (isEdit && villageId) {
                dispatch(
                    updateVillageAction({
                        ...payload,
                        endPoint: `geography/village/${villageId}`,
                    })
                );
            } else {
                dispatch(createVillageAction(payload));
            }
        },
    });
    // Duplicate check as user types
    useEffect(() => {
        if (!formik.values.name || !formik.values.mandalId) {
            setDuplicateError("");
            return;
        }
        const newNameNorm = normalizeVillageName(formik.values.name);
        const duplicate = villages.some((v) => {
            const vName = v?.name || v?.village_name || v?.villageName || "";
            const vMandalId = v?.mandalId || v?.mandal_id || v?.mandalID;
            const vId = v?.villageId || v?.id || v?.village_id;
            if (isEdit && String(vId) === String(villageId)) return false;
            return String(vMandalId) === String(formik.values.mandalId) && normalizeVillageName(vName) === newNameNorm;
        });
        setDuplicateError(duplicate ? "Village with this name already exists in this mandal!" : "");
    }, [formik.values.name, formik.values.mandalId, villages, isEdit, villageId]);

    // Fetch mandals when dialog opens
    useEffect(() => {
        if (open) dispatch(getAllMandalsAction());
    }, [open, dispatch]);

    // Fetch village by id when editing and dialog opens
    useEffect(() => {
        if (open && isEdit && villageId) {
            dispatch(getVillageByIdAction({ endPoint: `geography/village/${villageId}` }));
        }
    }, [open, isEdit, villageId, dispatch]);

    // Prefill when village-by-id fetched
    useEffect(() => {
        if (open && isEdit && villageByIdData && !villageByIdLoading && !villageByIdError) {
            const v = villageByIdData?.data?.village || villageByIdData?.data || villageByIdData;
            const prefill = {
                name: v?.name || v?.village_name || v?.villageName || "",
                mandalId: String(v?.mandalId ?? v?.mandalID ?? v?.mandal_id ?? ""),
            };
            formik.setValues(prefill);
        }
    }, [open, isEdit, villageByIdData, villageByIdLoading, villageByIdError]);

    // Handle create success/error
    useEffect(() => {
        if (!open) return;
        if (createSuccess) {
            toast.success("Village created successfully!");
            formik.resetForm();
            dispatch(clearCreateVillageAction());
            onSuccess?.();
            onClose();
        }
        if (createError) {
            toast.error(createErrorInfo || "Failed to create village");
            dispatch(clearCreateVillageAction());
        }
    }, [open, createSuccess, createError, createErrorInfo, dispatch, formik, onClose, onSuccess]);

    // Handle update success/error
    useEffect(() => {
        if (!open) return;
        if (updateSuccess) {
            toast.success("Village updated successfully!");
            formik.resetForm();
            dispatch(clearUpdateVillageAction());
            onSuccess?.();
            onClose();
        }
        if (updateError) {
            toast.error(updateErrorInfo || "Failed to update village");
            dispatch(clearUpdateVillageAction());
        }
    }, [open, updateSuccess, updateError, updateErrorInfo, dispatch, formik, onClose, onSuccess]);

    const loading = createLoading || updateLoading || villageByIdLoading;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            slotProps={{
                paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 4 } } },
            }}
        >
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
                <CloseIcon />
            </IconButton>

            <DialogTitle sx={{ p: 0, mb: 1 }}>
                <Typography fontSize={28} fontWeight={700}>Village Information</Typography>
                <Typography fontSize={14} color="text.secondary">{isEdit ? "Update Village" : "Create new Village"}</Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 0, mt: 3 }}>
                <form onSubmit={formik.handleSubmit}>
                    <Box mb={2}>
                        <Typography fontSize={14} fontWeight={600} mb={1}>Mandal</Typography>
                        <Autocomplete
                            options={mandals.map((m) => ({
                                id: m.mandalId || m.id || m.mandal_id || m.mandalID,
                                name: m.mandal_name || m.mandalName || m.name || String(m.mandalId || m.id || m.mandal_id || m.mandalID || ""),
                            })).filter((o) => o.id)}
                            value={(function () {
                                const currentId = formik.values.mandalId;
                                if (!currentId) return null;
                                const found = mandals.find((m) => {
                                    const mid = m.mandalId || m.id || m.mandal_id || m.mandalID;
                                    return String(mid) === String(currentId);
                                });
                                return found
                                    ? {
                                          id: found.mandalId || found.id || found.mandal_id || found.mandalID,
                                          name:
                                              found.mandal_name ||
                                              found.mandalName ||
                                              found.name ||
                                              String(found.mandalId || found.id || found.mandal_id || found.mandalID || ""),
                                      }
                                    : null;
                            })()}
                            onChange={(_, newValue) => {
                                formik.setFieldValue("mandalId", newValue ? String(newValue.id) : "");
                            }}
                            loading={mandalsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={mandalsLoading ? "Loading mandals..." : "Select a mandal"}
                                    fullWidth
                                    size="small"
                                    error={formik.touched.mandalId && Boolean(formik.errors.mandalId)}
                                    helperText={formik.touched.mandalId && formik.errors.mandalId}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                                />
                            )}
                            getOptionLabel={(option) => option?.name ?? ""}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        />
                    </Box>

                    <Box mb={4}>
                        <Typography fontSize={14} fontWeight={600} mb={1}>Village Name</Typography>
                        <TextField
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            fullWidth
                            size="small"
                            placeholder="Enter Village"
                            error={!!duplicateError || (formik.touched.name && Boolean(formik.errors.name))}
                            helperText={duplicateError ? duplicateError : (formik.touched.name && formik.errors.name)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                        />
                    </Box>

                    <Box display="flex" gap={2} flexDirection="row">
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{
                                borderRadius: 3,
                                fontWeight: 600,
                                p: "10px 0px",
                                color: "#000",
                                border: "1px solid #000",
                                textTransform: "none",
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading || !!duplicateError}
                            sx={{
                                borderRadius: 3,
                                p: "10px 0px",
                                fontWeight: 600,
                                textTransform: "none",
                                bgcolor: "#5B4CF5",
                                boxShadow: "0px 8px 20px rgba(91,76,245,0.3)",
                                "&:hover": { bgcolor: "#4a3de0" },
                            }}
                        >
                            {loading ? (isEdit ? "Updating..." : "Submitting...") : isEdit ? "Update Village" : "Submit Data"}
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
};


AddVillage.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    isEdit: PropTypes.bool,
    villageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuccess: PropTypes.func,
    mandalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    allowedMandals: PropTypes.arrayOf(PropTypes.string),
};

AddVillage.defaultProps = {
    isEdit: false,
    villageId: "",
    onSuccess: undefined,
    mandalId: "",
    allowedMandals: [],
};

export default AddVillage;